import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BETREIBER, FACHADMIN, SYSTEMADMIN, ZUST_SBS } from "../../../consts/funktionaleRollen";
import {
    deleteJahresberichtExterneDateiEndpoint, getJahresberichtByIDEndpoint,
    getJahresberichtDateienEndpoint,
    getJahresberichtDateiUploadEndpoint,
    getJahresberichtExterneDateienEndpoint,
    getJahresberichtExterneDateiUploadEndpoint
} from "../../../services/AnlagenService";
import { getDateiDownloadDateiEndpoint, getDeleteDateiEndpoint } from "../../../services/DateiService";
import { handleApiException, useHttp } from "../../../services/HttpService";
import { createCalculatedColumn, createColumn, createComponentColumn, createDateColumn, NUMBERFORMAT } from "../../../utils/ColumnUtils";
import { userHasAnyRole } from "../../../utils/SecurityUtils";
import { confirmDeleteOption } from "../../ConfirmationDialog/confirmationDialogOptions";
import { useConfirmation } from "../../ConfirmationDialog/ConfirmationDialogProvider";
import EAButton from "../../EAButton";
import EAButtonBar from "../../EAButtonBar";
import EADownloadButton from "../../EADownloadButton";
import EAFileUploadDialog from "../../EAFileUploadDialog";
import CollapsibleTable from "../../tableComponents/CollapsibleTable";
import EATableActionButton from "../../tableComponents/EATableActionButton";

export default function JahresberichtDateien({ modus, setToggleAllowed }) {
    const confirm = useConfirmation();

    const [berichtID, setBerichtID] = useState("");
    const [bericht, refetchBericht] = useHttp(getJahresberichtByIDEndpoint(berichtID), {manual: true});

    const [uploadPathDateien, setUploadPathDateien] = useState();
    const [uploadPathExterne, setUploadPathExterne] = useState();
    const [uploadDlgDateienOpen, setFileUploadDlgDateienOpen] = useState(false);
    const [uploadDlgExterneOpen, setFileUploadDlgExterneOpen] = useState(false);

    const [refetchUrlDateien, setRefetchUrlDateien] = useState();
    const [refetchUrlExterne, setRefetchUrlExterne] = useState();
    const [filesDateien, refetchFilesDateien] = useHttp(refetchUrlDateien, { manual: true });
    const [filesExterne, refetchFilesExterne] = useHttp(refetchUrlExterne, { manual: true });
    const [{ loading: deleting }, deleteFile] = useHttp({ method: "DELETE" }, { manual: true });
    const [readonly, setReadonly] = useState(true);

    extractBerichtID();

    function deleteDatei(url) {
        confirm(
            confirmDeleteOption(
                "die Datei",
                () =>
                    deleteFile({ url: url })
                        .then(() => {
                            refetchFilesDateien().catch((e) => handleApiException(e, "Error refetching files after delete"));
                            refetchFilesExterne().catch((e) => handleApiException(e, "Error refetching files after delete"));
                            toast.success("Die Datei wurde erfolgreich gelöscht.");
                        })
                        .catch((e) => handleApiException(e, "Error while trying to delete File")),
                () => {}
            )
        );
    }

    const fileColumns = [
        createComponentColumn(
            "bezeichnung",
            (row, index) => (
                <EADownloadButton
                    id={"downloadFile" + index}
                    label={row.bezeichnung}
                    url={getDateiDownloadDateiEndpoint(row.id)}
                    variant="text"
                    color="primary"
                    style={{ background: "transparent" }}
                />
            ),
            "Bezeichnung"
        ),
        createColumn("uploadVon", "Hochgeladen von"),
        createDateColumn("uploadAm", "Datum"),
        createCalculatedColumn(
            "dateigroesse",
            (row) => NUMBERFORMAT.format(parseFloat((row.dateigroesse / Math.pow(1024, 2)).toFixed(3))),
            "Größe [MB]"
        ),
        createCalculatedColumn("dateityp", (row) => row.dateityp.value, "Dateityp"),
    ];

    const fileColumnsWithDeleteDateien = [
        createComponentColumn("actionColumn", (row, index) => (
            <EATableActionButton id={"dateiDeleteAction-" + index} iconName="ico-delete">
                <span onClick={() => deleteDatei(getDeleteDateiEndpoint(row.id))} />
            </EATableActionButton>
        )),
        ...fileColumns,
    ];

    const fileColumnsWithDeleteExterne = [
        createComponentColumn("actionColumn", (row, index) => (
            <EATableActionButton id={"dateiDeleteAction-" + index} iconName="ico-delete">
                <span onClick={() => deleteDatei(deleteJahresberichtExterneDateiEndpoint(berichtID))} />
            </EATableActionButton>
        )),
        ...fileColumns,
    ];

    //
    function extractBerichtID() {
        const currentURL = new URL(window.location.href);
        const pathParts = currentURL.pathname.split('/');
        const jahresberichtIndex = pathParts.indexOf('jahresbericht');

        if (jahresberichtIndex !== -1 && jahresberichtIndex < pathParts.length - 1) {
            let id = pathParts[jahresberichtIndex + 1];
            if (id !== berichtID) {
                setBerichtID(id);
            }
        }
    }

    useEffect(() => {
        if (refetchUrlDateien) {
            refetchFilesDateien().catch((e) => handleApiException(e, "Error refetching files"));
        }
        if (refetchUrlExterne) {
            refetchFilesExterne().catch((e) => handleApiException(e, "Error refetching files"));
        }
    }, [refetchUrlDateien, refetchUrlExterne, refetchFilesDateien, refetchFilesExterne]);

    useEffect(() => {
        setReadonly(modus !== "s");
        if (berichtID) {
            setRefetchUrlDateien(getJahresberichtDateienEndpoint(berichtID));
            setRefetchUrlExterne(getJahresberichtExterneDateienEndpoint(berichtID));
            setUploadPathDateien(getJahresberichtDateiUploadEndpoint(berichtID));
            setUploadPathExterne(getJahresberichtExterneDateiUploadEndpoint(berichtID));
            refetchBericht();
        }
    }, [berichtID, modus, refetchFilesDateien, refetchFilesExterne, refetchBericht]);

    useEffect(() => {
        var isEditAllowed = false;
        if (bericht?.data) {
            const isPlausibilisiert = bericht.data.status === "PLAUSIBILISIERT";
            const isEingereicht = bericht.data.status === "EINGEREICHT";

            isEditAllowed = userHasAnyRole([BETREIBER, ZUST_SBS, FACHADMIN, SYSTEMADMIN])
                && (!userHasAnyRole(BETREIBER) || (userHasAnyRole(BETREIBER) && !isEingereicht))
                && !isPlausibilisiert
        }

        if (isEditAllowed) {
            setToggleAllowed(true);
        } else {
            setToggleAllowed(false);
        }
    }, [setToggleAllowed, bericht]);

    function handleFileUploadSuccessed() {
        setFileUploadDlgDateienOpen(false);
        setFileUploadDlgExterneOpen(false);
        refetchFilesDateien().catch((e) => handleApiException(e, "Error refetching files after Upload"));
        refetchFilesExterne().catch((e) => handleApiException(e, "Error refetching files after Upload"));
        toast.success("Die Datei wurde erfolgreich hochgeladen");
    }

    function handleFileUploadError(error) {
        toast.error(error ? error : "Die Datei konnte nicht hochgeladen werden!");
    }

    return (
        <div className="tabsheet-content-margin" style={{ gridTemplateRows: "max-content min-content" }}>
            <EAFileUploadDialog
                open={uploadDlgDateienOpen}
                testId="dateiUploadDlg"
                label="Datei hochladen"
                onClose={() => setFileUploadDlgDateienOpen(false)}
                uploadPath={uploadPathDateien}
                onSuccess={handleFileUploadSuccessed}
                onError={handleFileUploadError}
            />
            {
                <CollapsibleTable
                    id="dateien-table"
                    label="Dateien"
                    loading={filesDateien.loading || deleting || !bericht?.data}
                    columns={readonly ? fileColumns : fileColumnsWithDeleteDateien}
                    data={filesDateien.data}
                />
            }
            {!readonly && (
                <EAButtonBar>
                    <EAButton
                        id="openFileUploadDlgButton"
                        label="Datei hochladen"
                        onClick={() => setFileUploadDlgDateienOpen(true)}
                        disabled={!uploadPathDateien}
                        loading={!uploadPathDateien}
                    />
                </EAButtonBar>
            )}
            <div style={{ marginTop: "10px" }}>
                <EAFileUploadDialog
                    open={uploadDlgExterneOpen}
                    testId="dateiUploadDlg"
                    label="Datei hochladen"
                    onClose={() => setFileUploadDlgExterneOpen(false)}
                    uploadPath={uploadPathExterne}
                    onSuccess={handleFileUploadSuccessed}
                    onError={handleFileUploadError}
                />
                {
                    <CollapsibleTable
                        id="dateien-table"
                        label="Externe Stoffe"
                        loading={filesExterne.loading || deleting || !bericht?.data}
                        columns={readonly ? fileColumns : fileColumnsWithDeleteExterne}
                        data={filesExterne.data}
                    />
                }
                {!readonly && (
                    <EAButtonBar>
                        <EAButton
                            id="openFileUploadDlgButton"
                            label="Datei hochladen"
                            onClick={() => setFileUploadDlgExterneOpen(true)}
                            disabled={!uploadPathExterne}
                            loading={!uploadPathExterne}
                        />
                    </EAButtonBar>
                )}
            </div>
        </div>
    );
}
